import React, { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function WelcomeLogin() {
  const { t, lang, setLang } = useI18n();
  const { gateLogin } = useAuth();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error(t.gate.needName); return; }
    if (!code.trim()) { toast.error(t.gate.needCode); return; }
    setLoading(true);
    try {
      await gateLogin(name.trim(), code.trim());
      // gate auto re-renders to the actual app
    } catch (err) {
      toast.error(formatApiError(err) || t.gate.invalidCode);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="welcome-login"
      className="min-h-screen flex flex-col"
      style={{ background: "var(--bg-blush)" }}
    >
      {/* Lang switch top-right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 z-10">
        <button
          onClick={() => setLang("es")}
          data-testid="gate-lang-es"
          aria-label="Español"
          className={`w-9 h-9 rounded-full border text-[0.7rem] font-bold tracking-wider transition-all ${
            lang === "es"
              ? "bg-[color:var(--olive-deep)] text-white border-[color:var(--olive-deep)]"
              : "bg-white text-[color:var(--ink-wine)] border-[color:var(--line)] hover:border-[color:var(--ink-wine)]"
          }`}
        >ES</button>
        <button
          onClick={() => setLang("ca")}
          data-testid="gate-lang-ca"
          aria-label="Català"
          className={`w-9 h-9 rounded-full border text-[0.7rem] font-bold tracking-wider transition-all ${
            lang === "ca"
              ? "bg-[color:var(--olive-deep)] text-white border-[color:var(--olive-deep)]"
              : "bg-white text-[color:var(--ink-wine)] border-[color:var(--line)] hover:border-[color:var(--ink-wine)]"
          }`}
        >CA</button>
      </div>

      {/* Hero text */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-20 pb-10">
        <p
          className="text-[0.75rem] sm:text-[0.85rem] tracking-[0.35em] uppercase text-[color:var(--ink-soft)] mb-6"
          data-testid="gate-eyebrow"
        >
          {t.gate.welcome}
        </p>
        <h1
          className="font-script text-[3.5rem] sm:text-[5rem] leading-none text-[color:var(--ink-wine)] mb-6"
          data-testid="gate-title"
        >
          Selena &amp; Mario
        </h1>
        <div className="w-16 h-px bg-[color:var(--gold,#c8a55e)] mb-8 opacity-80" />
        <p className="font-serif italic text-base sm:text-lg text-[color:var(--ink-deep)] mb-6 max-w-xl">
          {t.gate.tagline}
        </p>
        <p className="text-[0.7rem] sm:text-[0.8rem] tracking-[0.32em] font-semibold uppercase text-[color:var(--ink-wine)]/80">
          {t.gate.when}
        </p>
      </section>

      {/* Bottom login card */}
      <section className="px-3 sm:px-6 pb-6">
        <form
          onSubmit={onSubmit}
          data-testid="gate-form"
          className="max-w-2xl mx-auto bg-white rounded-3xl shadow-lg p-6 sm:p-8 space-y-5 border border-[color:var(--line)]"
        >
          <div>
            <label className="text-[0.7rem] tracking-[0.28em] font-bold uppercase text-[color:var(--ink-deep)] block mb-2">
              {t.gate.nameLabel}
            </label>
            <input
              data-testid="gate-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.gate.namePh}
              className="w-full rounded-2xl border border-[color:var(--line)] bg-[color:var(--bg-blush)] px-4 sm:px-5 py-3 sm:py-4 text-base font-serif italic text-[color:var(--ink-deep)] placeholder:text-[color:var(--ink-soft)]/70 focus:outline-none focus:border-[color:var(--olive)] focus:ring-2 focus:ring-[color:var(--olive)]/20"
              autoComplete="given-name"
              required
            />
          </div>

          <div>
            <label className="text-[0.7rem] tracking-[0.28em] font-bold uppercase text-[color:var(--ink-deep)] block mb-2">
              {t.gate.codeLabel}
            </label>
            <input
              data-testid="gate-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t.gate.codePh}
              className="w-full rounded-2xl border border-[color:var(--line)] bg-[color:var(--bg-blush)] px-4 sm:px-5 py-3 sm:py-4 text-base font-serif italic text-[color:var(--ink-deep)] placeholder:text-[color:var(--ink-soft)]/70 focus:outline-none focus:border-[color:var(--olive)] focus:ring-2 focus:ring-[color:var(--olive)]/20"
              autoComplete="off"
              autoCapitalize="characters"
              required
            />
            <p className="text-xs italic text-[color:var(--ink-soft)] mt-2">{t.gate.help}</p>
          </div>

          <button
            type="submit"
            data-testid="gate-submit"
            disabled={loading}
            className="w-full rounded-2xl bg-[color:var(--olive-deep)] hover:bg-[color:var(--olive)] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm tracking-[0.28em] font-bold uppercase py-4 sm:py-5 transition-colors"
          >
            {loading ? t.gate.submitting : t.gate.submit}
          </button>
        </form>
      </section>
    </main>
  );
}
